import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    Container, Typography, Card, CardContent, Button, Grid, Box,
    AppBar, Toolbar, Select, MenuItem, Chip, CircularProgress, Alert
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LaunchIcon from '@mui/icons-material/Launch';
import axios from 'axios';
import { LanguageContext } from '../App';
import { LANGUAGES } from '../utils/translations';
import logo from '../assets/logo.png';

const GovernmentSchemes = () => {
    const navigate = useNavigate();
    const { language, setLanguage } = useContext(LanguageContext);
    const [schemes, setSchemes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedState, setSelectedState] = useState('All India');
    const [states, setStates] = useState(['All India']);

    const fetchSchemes = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get(`/api/government/schemes?state=${selectedState}&lang=${language}`);
            setSchemes(response.data.schemes);
            if (response.data.states && states.length === 1) {
                setStates(['All India', ...response.data.states]);
            }
        } catch (error) {
            console.error('Error fetching schemes:', error);
        } finally {
            setLoading(false);
        }
    }, [selectedState, language, states.length]);

    useEffect(() => {
        fetchSchemes();
    }, [fetchSchemes]);

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: { y: 0, opacity: 1 }
    };

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: '#000000' }}>
            <AppBar position="sticky" elevation={0} sx={{ top: 0, zIndex: 1100, backgroundColor: 'rgba(0, 0, 0, 0.2)', backdropFilter: 'blur(20px)', borderBottom: 'none' }}>
                <Toolbar>
                    <img src={logo} alt="SoilSense" style={{ height: '60px', marginRight: '16px' }} />
                    <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: '#4CAF50', fontWeight: 600 }}>
                        Govt. Schemes
                    </Typography>
                    <Select
                        value={language}
                        onChange={(e) => setLanguage(e.target.value)}
                        size="small"
                        sx={{ color: '#4CAF50', mr: 2, '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(76, 175, 80, 0.5)' } }}
                    >
                        {LANGUAGES.map((lang) => (
                            <MenuItem key={lang.code} value={lang.code}>{lang.nativeName}</MenuItem>
                        ))}
                    </Select>
                    <Button
                        color="inherit"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate('/')}
                        sx={{ color: '#4CAF50' }}
                    >
                        Back
                    </Button>
                </Toolbar>
            </AppBar>

            <Container maxWidth="lg" sx={{ mt: 4, pb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                    <Typography variant="h4" className="text-shimmer" sx={{ fontWeight: 'bold' }}>
                        Active Government Schemes
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography sx={{ color: '#888' }}>Select State:</Typography>
                        <Select
                            value={selectedState}
                            onChange={(e) => setSelectedState(e.target.value)}
                            size="small"
                            sx={{
                                color: '#00BCD4',
                                minWidth: 200,
                                '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 188, 212, 0.3)' },
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#00BCD4' }
                            }}
                        >
                            {states.map(state => (
                                <MenuItem key={state} value={state}>{state}</MenuItem>
                            ))}
                        </Select>
                    </Box>
                </Box>

                <Alert severity="info" sx={{ mb: 4, backgroundColor: 'rgba(0, 188, 212, 0.1)', color: '#e0f7fa', '& .MuiAlert-icon': { color: '#00BCD4' } }}>
                    Real-time feed of newly released government schemes relevant to your region. Showing data in <strong>{LANGUAGES.find(l => l.code === language).name}</strong>.
                </Alert>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                        <CircularProgress sx={{ color: '#00BCD4' }} />
                    </Box>
                ) : (
                    <Grid container spacing={3} component={motion.div} variants={containerVariants} initial="hidden" animate="visible">
                        {schemes.map((scheme, index) => (
                            <Grid item xs={12} md={6} key={index} component={motion.div} variants={itemVariants}>
                                <Card className="premium-card" sx={{ height: '100%', borderLeft: '4px solid #00BCD4' }}>
                                    <CardContent>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                            <Typography variant="h6" sx={{ color: '#00BCD4', fontWeight: 'bold' }}>
                                                {scheme.title}
                                            </Typography>
                                            <Chip
                                                label={scheme.category}
                                                size="small"
                                                sx={{ backgroundColor: 'rgba(0, 188, 212, 0.2)', color: '#00BCD4', fontWeight: 'bold' }}
                                            />
                                        </Box>

                                        <Typography variant="body1" sx={{ color: '#fff', mb: 2, fontWeight: 500 }}>
                                            {scheme.highlight}
                                        </Typography>

                                        <Box sx={{ mb: 3 }}>
                                            {scheme.bullets.map((bullet, i) => (
                                                <Typography key={i} variant="body2" sx={{ color: '#aaa', display: 'flex', gap: 1, mb: 0.5 }}>
                                                    • {bullet}
                                                </Typography>
                                            ))}
                                        </Box>

                                        <Box sx={{ display: 'flex', gap: 2, mt: 'auto' }}>
                                            <Button
                                                variant="contained"
                                                startIcon={<LaunchIcon />}
                                                href={scheme.link}
                                                target="_blank"
                                                sx={{ backgroundColor: '#00BCD4', '&:hover': { backgroundColor: '#0097a7' } }}
                                            >
                                                Apply Now
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                href={scheme.link}
                                                target="_blank"
                                                sx={{ borderColor: '#00BCD4', color: '#00BCD4', '&:hover': { borderColor: '#fff', color: '#fff' } }}
                                            >
                                                Learn More
                                            </Button>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Container>
        </Box>
    );
};

export default GovernmentSchemes;
